import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { Tooltip } from 'bootstrap';
import App from './App.vue';

/*
TIP: To get started with clean router change path to @/router/clean.ts.
 */
import router from './router';
import i18n from '@/plugins/i18n';

import DatePicker from 'vue3-persian-datetime-picker';

//imports for app initialization
// Note: ApiService no longer needs an app-instance init step here — as part of the
// dashboard work it was refactored into a standalone module (its own axios instance
// plus a session-expired-alert guard, see src/services/ApiService.ts) so it keeps
// working across concurrent background requests (e.g. the dashboard's polling
// widgets, src/composables/usePolling.ts) without being wired into this Vue `app`.
import { initApexCharts } from '@/plugins/apexcharts';
import { initInlineSvg } from '@/plugins/inline-svg';
import { initKtIcon } from '@/plugins/keenthemes';
import * as Sentry from '@sentry/vue';
import JsonExcel from 'vue-json-excel3';
import { initSessionRecording } from '@/plugins/openreplay';

// global component
import Loading from '@/components/widget/PageLoading.vue';
import { useVersion } from '@/composables/version';

declare const __APP_VERSION__: string;

function mountApp() {
  const app = createApp(App);

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: __APP_VERSION__,
    environment: import.meta.env.VITE_SENTRY_ENV,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    tracePropagationTargets: ['localhost', import.meta.env.VITE_APP_API_URL],
    // Session Replay
    replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
    replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE),
  });

  // Runs regardless of auth state, so it also covers anonymous/pre-login
  // activity (e.g. sign-in trouble) — see identifySessionUser() for where
  // logged-in identity gets attached. Must run after Sentry.init() so the
  // OpenReplay session tags it sets land on an initialized Sentry client.
  initSessionRecording();

  const pinia = createPinia();
  app.use(pinia);
  app.use(router);
  app.component('loading-layer', Loading);
  app.component('DatePicker', DatePicker);
  app.component('downloadExcel', JsonExcel);

  initApexCharts(app);
  initInlineSvg(app);
  initKtIcon(app);

  app.use(i18n);

  app.directive('tooltip', {
    mounted(el) {
      el._tooltip = new Tooltip(el);
    },
    unmounted(el) {
      el._tooltip?.dispose();
    },
  });

  app.mount('#app');
}

// Check for a new version before mounting. The splash screen is still visible
// at this point so the user sees a normal loading experience even if a reload
// is triggered. useVersion() only uses plain async functions – no Vue context needed.
(async () => {
  const { checkVersion, performUpdate } = useVersion();

  try {
    console.log('[Update] 🔍 Checking for new version…');
    const versionData = await checkVersion();
    if (versionData) {
      console.log('[Update] 🆕 New version detected on page load, updating…');
      await performUpdate(versionData);
      return; // page will reload – stop here
    }
    console.log('[Update] ✅ App is up to date');
  } catch (e) {
    console.warn('[Update] ⚠️ Version check failed, proceeding with app mount:', e);
  }

  mountApp();
})();
